/**
 * Reading statistics and analytics API methods for ApiClient (mixin pattern).
 * These functions use `this` and are assigned to ApiClient.prototype.
 */

import { API_BASE, fetchWithRetry } from './helpers';

/**
 * Push incremental reading time (and words read) to the backend.
 * Uses fetch keepalive so the request can survive page unload.
 */
export async function pushReadingTime(bookId, durationMs, words = 0, localDate, localHour, language, chapterHW, chapterWordCounts, rereadWords = 0) {
    if (!this.token || !bookId || durationMs < 1000) return;
    const now = new Date();
    const payload = {
        duration_ms: Math.round(durationMs),
        local_date: localDate || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
        local_hour: typeof localHour === 'number' ? localHour : now.getHours(),
    };
    // words = 新读 (coverage, position-based new reading). reread_words = 重温
    // (re-read over already-covered territory). Sent as separate buckets so the
    // backend stores the client's position-based split directly instead of
    // re-deriving 重温 from a volume number that doesn't actually contain it.
    if (words > 0) payload.words = Math.round(words);
    if (rereadWords > 0) payload.reread_words = Math.round(rereadWords);
    if (language) payload.language = language;
    if (chapterHW && typeof chapterHW === 'object') payload.chapter_hw = chapterHW;
    // D2-B: pair chapter_hw with chapter_word_counts so backend can MIN-clamp
    // per-chapter (defense-in-depth against frontend bugs). Only send when
    // client has synced server HW context (matches chapter_hw gating).
    if (chapterWordCounts && typeof chapterWordCounts === 'object') payload.chapter_word_counts = chapterWordCounts;
    const resp = await fetch(`${API_BASE}/reader/stats/${bookId}/reading-time`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true,
    });
    if (!resp.ok) {
        // Attach HTTP status so retryPending can distinguish permanent
        // validation failures (4xx — drop the pending entry) from transient
        // server/network errors (5xx — keep for retry). Without this, a
        // stale pending entry whose local_date is > 3 days old or whose ms
        // exceeds the 30-min backend cap would loop forever on every book
        // open, firing 400s at the user each session.
        const e = new Error(`pushReadingTime failed: ${resp.status}`);
        e.status = resp.status;
        throw e;
    }
}

/** Fetch dashboard summary (today, week, streak, yearly goal, milestones). */
export async function getDashboard() {
    if (!this.token) return null;
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const tzOffset = now.getTimezoneOffset();
    const resp = await fetchWithRetry(`${API_BASE}/reader/stats/dashboard?local_date=${localDate}&tz_offset=${tzOffset}`, {
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) throw new Error('getDashboard failed');
    return resp.json();
}

/** Fetch per-day word counts for heatmap (current year). */
export async function getHeatmap() {
    if (!this.token) return null;
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const resp = await fetchWithRetry(`${API_BASE}/reader/stats/heatmap?local_date=${localDate}`, {
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) throw new Error('getHeatmap failed');
    return resp.json();
}

/** Fetch analytics data for dashboard (trend, hourly WPM, per-book, overall). */
export async function getAnalytics(days = 30, language = '') {
    if (!this.token) return null;
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    let url = `${API_BASE}/reader/stats/analytics?local_date=${localDate}&days=${days}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    const resp = await fetchWithRetry(url, {
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) throw new Error('getAnalytics failed');
    return resp.json();
}

/** Fetch trend data for a specific date range (month navigation). */
export async function getTrend(localDate, days, signal, language = '') {
    if (!this.token) return [];
    const opts = { headers: { 'Authorization': `Bearer ${this.token}` } };
    if (signal) opts.signal = signal;
    let url = `${API_BASE}/reader/stats/analytics?local_date=${localDate}&days=${days}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    const resp = await fetchWithRetry(url, opts);
    if (!resp.ok) throw new Error('getTrend failed');
    const data = await resp.json();
    return { trend: data.trend || [], trend_by_language: data.trend_by_language || [] };
}

/** Fetch yearly analytics — monthly aggregates for bar/line charts. */
export async function getYearlyAnalytics(year, language = '') {
    if (!this.token) return null;
    const y = year || new Date().getFullYear();
    let url = `${API_BASE}/reader/stats/yearly?year=${y}`;
    if (language) url += `&language=${encodeURIComponent(language)}`;
    const resp = await fetchWithRetry(url, {
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) throw new Error('getYearlyAnalytics failed');
    return resp.json();
}

/** Fetch per-book WPM (all-time reading speed for each book). */
export async function getPerBookWpm() {
    if (!this.token) return {};
    try {
        const resp = await fetchWithRetry(`${API_BASE}/reader/stats/per-book-wpm`, {
            headers: { 'Authorization': `Bearer ${this.token}` },
        });
        if (!resp.ok) return {};
        const data = await resp.json();
        return data.books || {};
    } catch { return {}; }
}

/** Fetch per-book daily reading time (only days with activity). */
export async function getBookDailyStats(bookId) {
    if (!this.token || !bookId) return { days: [] };
    try {
        const resp = await fetchWithRetry(`${API_BASE}/reader/stats/${bookId}/daily`, {
            headers: { 'Authorization': `Bearer ${this.token}` },
        });
        if (!resp.ok) return { days: [] };
        return resp.json();
    } catch { return { days: [] }; }
}

/** Fetch per-chapter high water marks for position-based word dedup. */
export async function getChapterHW(bookId) {
    if (!this.token || !bookId) return null;
    try {
        const resp = await fetchWithRetry(`${API_BASE}/reader/stats/${bookId}/hw`, {
            headers: { 'Authorization': `Bearer ${this.token}` },
        });
        if (!resp.ok) return null;
        return resp.json();
    } catch { return null; }
}

/** Set or update the annual word-count goal. */
export async function setYearlyGoal(targetWords) {
    const resp = await fetch(`${API_BASE}/reader/goals/yearly`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ target_words: targetWords }),
    });
    const data = await resp.json().catch(() => ({}));
    if (data.error_code) return data;
    if (!resp.ok) throw new Error(data.error || 'setYearlyGoal failed');
    return data;
}

/** Fetch user settings from backend. */
export async function getSettings() {
    if (!this.token) return null;
    const resp = await fetch(`${API_BASE}/settings`, {
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return null;
    return resp.json();
}

/** Update user settings (reading goals, reminders, etc). */
export async function updateSettings(settings) {
    const resp = await fetch(`${API_BASE}/settings`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
    });
    const data = await resp.json().catch(() => ({}));
    if (data.error_code) return data;
    if (!resp.ok) throw new Error(data.error || 'updateSettings failed');
    return data;
}

/** Mark a milestone as notified (fire-and-forget). */
export function ackMilestone(key) {
    if (!this.token) return;
    fetch(`${API_BASE}/reader/milestones/${encodeURIComponent(key)}/ack`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.token}` },
    }).catch(() => {});
}

/** Get list of quote IDs the user has liked. */
export async function getQuoteLikes() {
    if (!this.token) return { likes: [] };
    const resp = await fetch(`${API_BASE}/reader/quote-likes`, {
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return { likes: [] };
    return resp.json();
}

/** Like a quote (3-table sync on backend). Returns `{ liked, count }` or `{ already, count }`. */
export async function likeQuote({ quote_id, genre, author_name, language }) {
    if (!this.token) return;
    const resp = await fetch(`${API_BASE}/reader/quote-likes`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quote_id, genre, author_name, language }),
    });
    if (!resp.ok) throw new Error('likeQuote failed');
    return resp.json();
}

/** Unlike a quote. Returns `{ unliked, count }` or `{ already, count }`. */
export async function unlikeQuote(quoteId) {
    if (!this.token) return;
    const resp = await fetch(`${API_BASE}/reader/quote-likes/${encodeURIComponent(quoteId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) throw new Error('unlikeQuote failed');
    return resp.json();
}

/** Get global like count for a single quote. Auth required — Reader is auth-only. */
export async function getQuoteLikeCount(quoteId) {
    if (!this.token || !quoteId) return { count: 0 };
    const resp = await fetch(`${API_BASE}/reader/quote-likes/count?id=${encodeURIComponent(quoteId)}`, {
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return { count: 0 };
    return resp.json();
}
